import React, {useState, useEffect, useRef} from 'react';
import {TextField} from '@mui/material';
import {
  Modal,
  Button,
  Headline,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import MarkdownFormattingHint from '@/components/markdown-formatting-hint';
import styles from './MessageCreatorsModal.module.scss';
import MessageCreatorsPreviewModal from './MessageCreatorsPreviewModal';
import MessageCreatorsSuccessModal from './MessageCreatorsSuccessModal';
import StackedAvatars from './StackedAvatars';
import {MESSAGE_MODAL_SX} from './constants';
import {STEP, Step, MessageCreator} from './types';

const DEFAULT_SUBJECT_PLACEHOLDER_KEY = 'e.g. New campaign opportunity';
const DEFAULT_BODY_PLACEHOLDER_KEY =
  "e.g. Hi {creator_name}, we have a campaign we think you'd love...";

interface MessageCreatorsModalProps {
  open: boolean;
  onClose: () => void;
  creators: MessageCreator[];
  totalCreatorCount?: number;
  onSend: (subject: string, body: string) => Promise<void>;
  subjectPlaceholder?: string;
  bodyPlaceholder?: string;
  onPlaceholderInsert?: (placeholder: string) => void;
  onStepChange?: (step: Step) => void;
}

function getSendButtonLabel(
  isSending: boolean,
  isSingleCreator: boolean,
  creatorCount: number
) {
  if (isSending) {
    return translate('Sending...');
  }
  if (isSingleCreator) {
    return translate('Send');
  }
  return translate('Send to {count} creators', {count: creatorCount});
}

function MessageCreatorsModal({
  open,
  onClose,
  creators,
  totalCreatorCount,
  onSend,
  subjectPlaceholder,
  bodyPlaceholder,
  onPlaceholderInsert,
  onStepChange,
}: MessageCreatorsModalProps) {
  const [step, setStep] = useState<Step>(STEP.COMPOSE);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sentCreators, setSentCreators] = useState<MessageCreator[]>([]);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const [isSending, setIsSending] = useState(false);

  const changeStep = (nextStep: Step) => {
    setStep(nextStep);
    onStepChange?.(nextStep);
  };

  useEffect(() => {
    if (!open) {
      setStep(STEP.COMPOSE);
      setSubject('');
      setBody('');
      setIsSending(false);
      setSentCreators([]);
    }
  }, [open]);

  const effectiveCount = totalCreatorCount ?? creators.length;
  const isSingleCreator = effectiveCount === 1;
  const isFormValid = subject.trim().length > 0 && body.trim().length > 0;
  const resolvedSubjectPlaceholder =
    subjectPlaceholder ?? translate(DEFAULT_SUBJECT_PLACEHOLDER_KEY);
  const resolvedBodyPlaceholder =
    bodyPlaceholder ?? translate(DEFAULT_BODY_PLACEHOLDER_KEY);

  const handleInsertName = () => {
    const token = '{creator_name}';
    onPlaceholderInsert?.(token);
    const textarea = bodyRef.current;
    if (!textarea) {
      setBody((prev) => `${prev}${token}`);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newBody = body.slice(0, start) + token + body.slice(end);
    setBody(newBody);
    requestAnimationFrame(() => {
      textarea.focus();
      const cursorPos = start + token.length;
      textarea.setSelectionRange(cursorPos, cursorPos);
    });
  };

  const doSend = async () => {
    setIsSending(true);
    setSentCreators(creators);
    try {
      await onSend(subject.trim(), body.trim());
      changeStep(STEP.SUCCESS);
    } catch {
      // onSend is responsible for surfacing the error to the user
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = async () => {
    if (isSingleCreator) {
      await doSend();
    } else {
      changeStep(STEP.PREVIEW);
    }
  };

  return (
    <>
      <Modal
        open={open && step === STEP.COMPOSE}
        handleClose={isSending ? undefined : onClose}
        showCloseButton
        size="large"
        sx={MESSAGE_MODAL_SX}
      >
        <div className={styles.modalContent}>
          <div className={styles.header}>
            <StackedAvatars creators={creators} />
            <Headline size="md">
              {isSingleCreator
                ? translate('Message {name}', {name: creators[0]?.displayName})
                : translate('Message {count} creators', {
                    count: effectiveCount,
                  })}
            </Headline>
          </div>
          <div className={styles.formArea}>
            <div className={styles.formSection}>
              <Label size="sm">{translate('Subject line')}</Label>
              <TextField
                fullWidth
                placeholder={resolvedSubjectPlaceholder}
                variant="outlined"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                autoFocus
                disabled={isSending}
              />
            </div>
            <div className={styles.fieldGroup}>
              <div className={styles.formSection}>
                <div className={styles.messageFieldHeader}>
                  <Label size="sm">{translate('Message')}</Label>
                  <Button
                    variant="outlined"
                    size="small"
                    color="info"
                    onClick={handleInsertName}
                    disabled={isSending}
                    startIcon={
                      <Icon name="Actions-Add-Small1" appearance="neutral" />
                    }
                  >
                    {translate('Insert name')}
                  </Button>
                </div>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder={resolvedBodyPlaceholder}
                  variant="outlined"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  disabled={isSending}
                  inputRef={bodyRef}
                />
              </div>
              <MarkdownFormattingHint />
            </div>
            <div className={styles.footer}>
              <Button
                mode="filled"
                appearance="neutral"
                size="large"
                onClick={handleSend}
                disabled={!isFormValid || isSending}
                isLoading={isSending}
              >
                {getSendButtonLabel(isSending, isSingleCreator, effectiveCount)}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {step === STEP.PREVIEW ? (
        <MessageCreatorsPreviewModal
          open={open}
          creatorCount={effectiveCount}
          subject={subject}
          body={body}
          onGoBack={() => changeStep(STEP.COMPOSE)}
          onConfirm={doSend}
          isSending={isSending}
        />
      ) : null}

      {step === STEP.SUCCESS ? (
        <MessageCreatorsSuccessModal
          open={open}
          creators={sentCreators}
          onDone={onClose}
        />
      ) : null}
    </>
  );
}

export default MessageCreatorsModal;
